<template>
  <div class="campaign-card">
    <div class="header">
      <img :src="imageSrc" alt="Campaign header" /><input v-if="props.mode !== 'preview'" type="file" id="image" accept="image/png, image/jpeg, image/gif" @change="previewImage" ref="uploadInput" />
      <label v-if="props.mode !== 'preview'" for="image" class="btn secondary no-dark-mode camp-toogle-theme-bt">
        <i class="bi bi-pencil" />
      </label>
    </div>
    <div class="body">
      <div v-if="props.mode == 'preview'">
        <div class="name-duration-row">
          <div class="block">
            <h4 class="card-title" v-text="model?.campaign?.headline" />
            <p class="job" v-text="model?.campaign?.job_title" />
          </div>
          <div class="informations-container">
            <div class="tag-container">
              <div class="tag" :style="getThemeStyle">{{ skillsStore.getAdventureDuration(props?.modelValue?.assessment?.modules || []) }} MIN</div>
            </div>
          </div>
        </div>
        <p class="campaign-description" v-safe-html="model?.campaign?.tagline" />
      </div>
      <div v-else>
        <div class="name-duration-row">
          <VField
            name="campaign-public-name"
            :placeholder="$t('app.campaign.card.campaignNamePlaceholder', {}, { locale: campaignLanguage })"
            :class="{
              invalid: error?.field === 'public_name' || model?.campaign?.headline?.length > 50,
            }"
            :modelValue="model?.campaign?.headline"
            @input="(event) => emits('changePublicName', event.target.value)"
          />
          <div class="informations-container">
            <div class="tag-container">
              <div class="tag" :style="getThemeStyle">{{ skillsStore.getAdventureDuration(props?.modelValue?.assessment?.modules || []) }} MIN</div>
            </div>
          </div>
        </div>
        <VField
          name="campaing-public-job"
          :placeholder="$t('app.campaign.card.contextPlaceholder', {}, { locale: campaignLanguage })"
          :class="{
            invalid: error?.field === 'job_title' || model?.campaign?.job_title?.length > 50,
          }"
          v-model="model.campaign.job_title"

        />
        <br />
        <QuillEditor class="quill-editor" id="campaign-public-description" v-model:content="content" contentType="html" :options="quillOptions" />
        <p v-if="error" class="red-color bold" v-text="getError(error.name, error.params)" />
      </div>
      <Button class="no-dark-mode campaign-action-button" :style="getThemeStyle" :label="$t('app.campaign.card.buttons.start', {}, { locale: campaignLanguage })" />
    </div>
    <div class="tooltip" v-if="showTooltip">
      <div>{{ $t("app.campaign.card.recommendation") }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import { getImage, getRandomCampaignPicture } from "@/composables/getImage.js";
import { CampaignCreation } from "@/types/campaign";

const { t, locale } = useI18n();

var content = ref("");

// Ref for the image src
const imageSrc = ref("");

const showTooltip = ref(false);

const handleContentChange = (newValue) => {
  model.value.campaign.tagline = newValue;
  emits("changeJobDescription", newValue);
};

watch(content, handleContentChange);

const emits = defineEmits(["changeImage", "changePublicName", "changeJobTitle", "changeJobDescription", "toggleError", "update:modelValue"]);

const toast = useToast();
const skillsStore = useSkillsStore();

const uploadInput = ref(null);

const props = defineProps<{
  mode: string,
  modelValue: CampaignCreation,
}>()

var imageChange = false;
showTooltip.value = props.mode == "new";

watch(
  () => props.mode,
  (newValue, oldValue) => {
    // React to prop changes
    if (!imageChange) {
      showTooltip.value = props.mode == "new";
    }
  },
);

const getThemeStyle = computed(() => {
  var bgColour = "#f2cb36";
  var textColour = "#000000";

  return {
    "background-color": model?.value?.branding?.highlight_color ? model.value.branding.highlight_color : bgColour,
    color: model?.value?.branding?.text_color ? model.value.branding.text_color : textColour,
  };
});

var imageWatcherEnabled = true;

// watch(
//   () => props.modelValue,
//   (newCampaign, oldValue) => {
//     if (newCampaign && imageWatcherEnabled) {
//       if (newCampaign.branding.banner_image_id) {
//         imageSrc.value = getImage(newCampaign.branding.banner_image_id);

//         // Reset the file input element so that any new file can be uploaded, even the same one
//         if (uploadInput.value) {
//           uploadInput.value.value = null; // This ensures the input is reset if needed
//         }

//         showTooltip.value = false;
//         imageChange = true;
//       } else {
//         //put a generic image again
//         imageSrc.value = getRandomCampaignPicture(newCampaign.campaign.internal_name);
//         imageChange = false;
//       }
//     }
//   },
//   { immediate: true, deep: true }, // This makes it run on initial load as well
// );

function checkProperty(property, max, field) {
  if (property === "") {
    emits("toggleError", {
      name: "ERROR_REQUIRED_FIELD",
    });

    return {
      name: "ERROR_REQUIRED_FIELD",
      field,
    };
  }

  if (property?.length > max) {
    emits("toggleError", {
      name: "ERROR_TOO_LONG",
      params: { size: max },
    });

    return {
      name: "ERROR_TOO_LONG",
      params: { size: max },
      field,
    };
  }

  return null;
}

const model = computed({
  get: () => props.modelValue,
  set: (value: CampaignCreation) => {
    emits('update:modelValue', value);
  }
});

const error = computed(() => {
  if (!props.modelValue) return null;
  if (checkProperty(model.value.campaign.headline, 50, "public_name")) return checkProperty(model.value.campaign.headline, 50, "public_name");

  // if (checkProperty(model.value.campaign.job_title, 50, "job_title")) return checkProperty(model.value.campaign.job_title, 50, "job_title");

  emits("toggleError", null);

  return null;
});

async function previewImage() {
  const [file] = uploadInput.value.files;

  const maxSize = 1024 * 2048; // 2Mo

  if (!["image/png", "image/jpeg", "image/gif"].includes(file.type)) {
    toast.error(t('other.error.ERROR_UNSUPPORTED_FORMAT'));
    return;
  }

  if (file.size > maxSize) {
    toast.error(t('other.error.IMAGE_TOO_HEAVY', { size: 2 }));
    return;
  }

  imageSrc.value = URL.createObjectURL(file);
  showTooltip.value = false;

  imageChange = true;
  imageWatcherEnabled = false; //disable image changing watcher as image changed in component

  emits("changeImage", file);
  setTimeout(() => {
    imageWatcherEnabled = true;
  }, 200); //enable it again
  // Reset the file input element to allow re-selecting the same file
  uploadInput.value.value = null;
}

const campaignLanguage = computed(() => {
  return model?.value?.campaign?.locale || 'fr';
});

const quillOptions = computed(() => {
  return {
    theme: "snow",
    placeholder: t("app.campaign.card.descriptionPlaceholder", {}, { locale: campaignLanguage.value }),
    modules: {
      toolbar: [["bold", "italic", "underline"], ["link"]],
    }
  }
});

onMounted(() => {
  // Initial setting of imageSrc
  if (props?.modelValue) {
    content.value = model.value.campaign.tagline ? model.value.campaign.tagline : "";
    if (props?.modelValue?.branding?.banner_image_id) {
      imageSrc.value = getImage(props.modelValue.branding.banner_image_id);
    } else {
      imageSrc.value = getRandomCampaignPicture(props.modelValue.campaign.internal_name);
    }
  }
});
</script>

<style>
.ql-toolbar {
  border: 1px solid #464646 !important;
  border-radius: 6px 6px 0px 0px;
}
.ql-editor {
  min-height: 130px;
  max-height: 130px;
  padding-top: 4px !important;
  font-size: 16px !important;
  font-family: 'Inter', 'Helvetica';

  -webkit-overflow-scrolling: auto; /* Fix scrollbar visibility in Safari ? */
  scrollbar-width: thin; 
  scrollbar-color: #c0bebe #464646; 
}
.ql-editor.ql-blank::before {
  color: white !important;
}
.ql-container {
  border: 1px solid #464646 !important;
  border-radius: 0px 0px 6px 6px;
}
.ql-tooltip {
  background: #1d1d1d !important;
  color: white !important;
}
.ql-snow .ql-stroke {
  stroke: white !important;
}

.quill-editor input[type="text"],
.quill-editor input[type="number"],
.quill-editor input[type="email"],
.quill-editor input[type="tel"],
.quill-editor input[type="password"] {
  color: white !important;
}

/* Change the color on hover */
.ql-toolbar .ql-formats button:hover {
  background-color: #464646 !important;
}
.ql-snow.ql-toolbar button.ql-active {
  background-color: #464646 !important;
}
</style>
